import * as ApiModels from '@typings/api-models';
import CommentContent from '@components/Message/CommentContent';
import CaretCircleRight from '@components/Icons/CaretCircleRight';
import FileDashed from '@components/Icons/FileDashed';
import Buttons from '@/components/Buttons';
import { useOpenDocumentRequestUserPane, useOpenDocumentRequestUserFilePane } from '@panes/hooks';

import Participants from './Participants';
import s from './Message.module.scss';

type Props = {
  message: Parameters<typeof CommentContent>[0]['comment'] & {
    contextType: string | null;
    contextId: string | null;
    contextTitle: string | null;
  };
  replies?: (Pick<ApiModels.Message, 'id'> & {
    owner: Pick<ApiModels.Person, 'id' | 'name'> & {
      primaryColour: { hex: string } | null;
      image: { url: string } | null;
    };
  })[];
  creator: Parameters<typeof CommentContent>[0]['creator'];
  onOpenDiscussion: () => void;
};

const ContextMessage: React.FC<Props> = ({ message, replies = [], creator, onOpenDiscussion }) => {
  const generateContextTag = (): { icon: React.ReactNode; tag: string } => {
    switch (message.contextType) {
    case 'document-request':
      return { icon: <FileDashed size={12} />, tag: message.contextTitle || 'Document Request' };
    case 'document-request-file':
      return { icon: <FileDashed size={12} />, tag: message.contextTitle || 'Document Request File' };
    default:
      return { icon: null, tag: '' };
    }
  };

  const { tag: contextTag, icon: contextIcon } = generateContextTag();

  const openDocumentRequestUserPane = useOpenDocumentRequestUserPane();
  const openDocumentRequestFileUserPane = useOpenDocumentRequestUserFilePane();

  return (
    <div className={s.contextMessage}>
      {message.contextId && (
        <Buttons
          isWrapper
          className={s.contextTag}
          onClick={() => {
            if (message.contextType === 'document-request' && message.contextId) {
              openDocumentRequestUserPane({ documentRequestUserId: message.contextId });
            }
            if (message.contextType === 'document-request-file' && message.contextId) {
              openDocumentRequestFileUserPane({ documentRequestUserFileId: message.contextId });
            }
          }}
        >
          {contextIcon}
          {contextTag}
        </Buttons>
      )}

      <div className={s.mainMessage}>
        <CommentContent comment={message} creator={creator} />
      </div>

      <div className={s.contextMessageFooter}>
        <div className={s.repliesContainer}>
          <Participants replies={replies} size={20} />
        </div>

        <Buttons isWrapper onClick={onOpenDiscussion}>
          Open discussion
          <CaretCircleRight size={12} weight="fill" />
        </Buttons>
      </div>
    </div>
  );
};

export default ContextMessage;
